import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Socket } from 'socket.io-client';
import { AlertTriangle, CircleCheck, CircleX, Eraser, LoaderCircle, Pointer, Search, SearchX, type LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import AppHeader from '@/components/AppHeader';
import Checkerboard from '@/components/packshots/Checkerboard';
import Dropzone from '@/components/packshots/Dropzone';
import PageUrlPicker from '@/components/packshots/PageUrlPicker';
import { useShotDefaults } from '@/components/packshots/ModelsDialog';
import { isInFlight, isServiceBusyError, jobDisplayName, progressLabel, statusHeadline } from '@/components/packshots/jobStatus';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  createBatch,
  JOB_STATUSES,
  jobFileUrl,
  listRecentJobs,
  partitionUploadFiles,
  uploadBatchJobs,
  type JobStatus,
  type PackshotJobCard,
  type SkippedFile,
  type UploadRejection,
} from '@/lib/packshots';
import { applyStageToCard, openPackshotSocket, stageEventSchema } from '@/lib/packshotSocket';
import { fetchSession, hasPermission } from '@/lib/session';
import { cn } from '@/lib/utils';

const RECENT_PAGE_SIZE = 12;

const STATUS_FILTERS: ReadonlyArray<{ value: JobStatus; label: string }> = [
  { value: 'needs_review', label: 'Needs a look' },
  { value: 'completed', label: 'Done' },
  { value: 'failed', label: 'Failed' },
  { value: 'queued', label: 'Queued' },
  { value: 'processing', label: 'Processing' },
  { value: 'sourcing', label: 'Searching the web' },
  { value: 'awaiting_pick', label: 'Pick a source' },
  { value: 'no_source_found', label: 'Nothing suitable found' },
];

function isJobStatus(value: string): value is JobStatus {
  return JOB_STATUSES.some((status) => status === value);
}

function statusMark(status: JobStatus): { icon: LucideIcon; className: string; spin: boolean } {
  switch (status) {
    case 'completed':
      return { icon: CircleCheck, className: 'bg-emerald-600 text-white', spin: false };
    case 'needs_review':
      return { icon: AlertTriangle, className: 'bg-amber-400 text-amber-950', spin: false };
    case 'awaiting_pick':
      return { icon: Pointer, className: 'bg-amber-400 text-amber-950', spin: false };
    case 'failed':
      return { icon: CircleX, className: 'bg-red-600 text-white', spin: false };
    case 'no_source_found':
      return { icon: SearchX, className: 'bg-red-600 text-white', spin: false };
    case 'queued':
    case 'processing':
    case 'sourcing':
      return { icon: LoaderCircle, className: 'bg-sky-600 text-white', spin: true };
    default: {
      const unreachable: never = status;
      return unreachable;
    }
  }
}

function StatusMark({ status, label, quiet }: { status: JobStatus; label: string; quiet?: boolean }) {
  const mark = statusMark(status);
  const Icon = mark.icon;
  return (
    <span
      title={label}
      className={cn('flex size-6 shrink-0 items-center justify-center rounded-full shadow-sm', mark.className)}
    >
      <Icon aria-hidden className={cn('size-3.5', mark.spin && 'animate-spin motion-reduce:animate-none')} />
      {!quiet && <span className="sr-only">{label}</span>}
    </span>
  );
}

function previewOf(job: PackshotJobCard): { url: string; output: boolean } | null {
  if (job.files.output) return { url: jobFileUrl(job.id, 'output', job.updatedAt), output: true };
  if (job.files.original) return { url: jobFileUrl(job.id, 'original', job.updatedAt), output: false };
  if (job.files.normalized) return { url: jobFileUrl(job.id, 'normalized', job.updatedAt), output: false };
  return null;
}

function RecentGrid({
  scope,
  query,
  status,
}: {
  scope: 'mine' | 'all';
  query: string;
  status: JobStatus | null;
}) {
  const [tiles, setTiles] = useState<PackshotJobCard[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);
  const cursor = useRef({ scope, query, status, page: 1, loading: false, done: false });

  const load = useCallback(async () => {
    const snap = cursor.current;
    if (snap.loading || snap.done || snap.scope !== scope || snap.query !== query || snap.status !== status) return;
    snap.loading = true;
    if (snap.page > 1) setLoadingMore(true);
    try {
      const result = await listRecentJobs(snap.scope, snap.page, RECENT_PAGE_SIZE, {
        q: snap.query.trim() || undefined,
        status: snap.status ?? undefined,
      });
      if (cursor.current !== snap) return;
      setTiles((current) => {
        const prev = snap.page === 1 ? [] : (current ?? []);
        const seen = new Set(prev.map((item) => item.id));
        return [...prev, ...result.items.filter((item) => !seen.has(item.id))];
      });
      setTotal(result.total);
      setError(null);
      snap.page += 1;
      snap.done = result.items.length < RECENT_PAGE_SIZE || (snap.page - 1) * RECENT_PAGE_SIZE >= result.total;
    } catch (err) {
      if (cursor.current !== snap) return;
      if (snap.page === 1) setTiles([]);
      setError(errorText(err, 'Could not load images'));
      snap.done = true;
    } finally {
      if (cursor.current === snap) {
        snap.loading = false;
        setLoadingMore(false);
      }
    }
  }, [scope, query, status]);

  useEffect(() => {
    const snap = { scope, query, status, page: 1, loading: false, done: false };
    cursor.current = snap;
    setTiles(null);
    setTotal(0);
    setError(null);
    setLoadingMore(false);
    void load();
  }, [scope, query, status, load]);

  useEffect(() => {
    const socket = openPackshotSocket();
    socketRef.current = socket;
    const onStage = (payload: unknown) => {
      const parsed = stageEventSchema.safeParse(payload);
      if (!parsed.success) return;
      setTiles((current) => current?.map((item) => applyStageToCard(item, parsed.data)) ?? current);
    };
    socket.on('packshot:stage', onStage);
    return () => {
      socket.off('packshot:stage', onStage);
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  const watchKey = (tiles ?? [])
    .filter((job) => isInFlight(job.status))
    .map((job) => job.id)
    .sort()
    .join(',');

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !watchKey) return;
    const ids = watchKey.split(',').filter((id) => id.length > 0);
    const watchAll = () => {
      for (const id of ids) {
        socket.emit('watch', { jobId: id });
      }
    };
    socket.on('connect', watchAll);
    if (socket.connected) watchAll();
    return () => {
      socket.off('connect', watchAll);
      for (const id of ids) socket.emit('unwatch', { jobId: id });
    };
  }, [watchKey]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || tiles === null || tiles.length >= total) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void load();
    }, { rootMargin: '280px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [tiles, total, load]);

  if (tiles === null) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {Array.from({ length: 8 }, (_, index) => (
          <Skeleton key={index} className="aspect-[480/655] w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (tiles.length === 0) {
    const filtered = query.trim().length > 0 || status !== null;
    return <p className="text-sm text-muted-foreground">{error ?? (filtered ? 'No images match.' : 'No images yet.')}</p>;
  }

  return (
    <div>
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {tiles.map((job) => {
          const preview = previewOf(job);
          const name = jobDisplayName(job);
          const displayStatus = job.status === 'failed' && isServiceBusyError(job.error?.message) ? 'queued' : job.status;
          const progress = progressLabel({ status: displayStatus, stage: job.stage });
          return (
            <li key={job.id}>
              <Link
                to={`/packshots/batches/${job.batchId}?job=${job.id}`}
                className="group block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <div className="relative aspect-[480/655] overflow-hidden rounded-lg bg-black">
                  {preview?.output && (
                    <img src={preview.url} alt="" className="h-full w-full object-contain" />
                  )}
                  {preview && !preview.output && (
                    <Checkerboard className="h-full w-full">
                      <img src={preview.url} alt="" className="h-full w-full object-contain" />
                    </Checkerboard>
                  )}
                  {!preview && (
                    <div className="flex h-full items-center justify-center px-3 text-center text-xs text-stone-300">
                      No image yet
                    </div>
                  )}
                  <div className="absolute left-2 top-2 z-10 flex max-w-[calc(100%-1rem)] items-center gap-1.5">
                    <StatusMark
                      status={displayStatus}
                      label={progress ?? statusHeadline(displayStatus)}
                      quiet={progress !== null}
                    />
                    {progress && (
                      <span className="min-w-0 truncate rounded-full bg-sky-600 px-2 py-1 text-xs font-medium text-white shadow-sm">
                        {progress}
                      </span>
                    )}
                  </div>
                </div>
                <p className="mt-1.5 truncate text-sm group-hover:underline">{name}</p>
              </Link>
            </li>
          );
        })}
      </ul>
      {tiles.length < total && !error && <div ref={sentinelRef} className="h-8" aria-hidden="true" />}
      {loadingMore && <p className="mt-3 text-sm text-muted-foreground">Loading more…</p>}
      {error && (
        <div className="mt-3 flex items-center gap-3">
          <p className="text-sm text-destructive">{error}</p>
          <Button
            type="button"
            variant="link"
            className="h-auto p-0"
            onClick={() => {
              cursor.current.done = false;
              setError(null);
              void load();
            }}
          >
            Try again
          </Button>
        </div>
      )}
    </div>
  );
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function toastSkipped(items: SkippedFile[]) {
  if (items.length === 0) return;
  toast.warning(items.length === 1 ? '1 file skipped' : `${items.length} files skipped`, {
    description: (
      <ul className="mt-1 list-disc pl-4">
        {items.map((item, index) => (
          <li key={`${item.name}-${index}`}>{item.name} skipped — {item.error}</li>
        ))}
      </ul>
    ),
  });
}

function toastRejections(items: UploadRejection[]) {
  if (items.length === 0) return;
  toast.warning(items.length === 1 ? '1 file rejected' : `${items.length} files rejected`, {
    description: (
      <ul className="mt-1 list-disc pl-4">
        {items.map((item, index) => (
          <li key={`${item.name}-${index}`}>{item.name} skipped — {item.error.message} ({item.error.code})</li>
        ))}
      </ul>
    ),
  });
}

export default function Packshots() {
  const navigate = useNavigate();
  const [canWrite, setCanWrite] = useState<boolean | null>(null);
  const { cleanupPolicy, cleanupPreset, cutoutPreset } = useShotDefaults();
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<JobStatus | null>(null);
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    void (async () => {
      const session = await fetchSession();
      setCanWrite(hasPermission(session, 'packshots.write'));
    })();
  }, []);

  async function onFiles(files: File[]) {
    const { accepted, skipped } = partitionUploadFiles(files);
    toastSkipped(skipped);
    if (accepted.length === 0) return;
    setBusy(true);
    try {
      const batch = await createBatch({
        cleanupPolicy,
        cleanupPreset: cleanupPreset || undefined,
        cutoutPreset: cutoutPreset || undefined,
      });
      try {
        const uploaded = await uploadBatchJobs(batch.id, accepted);
        toastRejections(uploaded.rejections);
      } catch (err) {
        toast.error(errorText(err, 'Could not upload these images'));
      }
      navigate(`/packshots/batches/${batch.id}`);
    } catch (err) {
      toast.error(errorText(err, 'Could not create the batch'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app-shell">
      <AppHeader />
      <main className="page-frame flex flex-1 flex-col">
        <Dropzone
          disabled={canWrite !== true}
          denied={canWrite === false}
          busy={busy}
          onFiles={(files) => { void onFiles(files); }}
        />
        {canWrite === true && (
          <p className="my-4 text-center text-sm text-muted-foreground">or</p>
        )}
        {canWrite === true && (
          <PageUrlPicker
            disabled={busy}
            cleanupPolicy={cleanupPolicy}
            cleanupPreset={cleanupPreset}
            cutoutPreset={cutoutPreset}
            onCreated={(batchId, rejections) => {
              toastRejections(rejections);
              navigate(`/packshots/batches/${batchId}`);
            }}
          />
        )}
        <section className="mt-10">
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative w-full sm:max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <Input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search shots"
                aria-label="Search shots"
                className="pl-9 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
              />
            </div>
            <Select
              value={status ?? 'all'}
              onValueChange={(next) => {
                if (next === 'all') {
                  setStatus(null);
                  return;
                }
                if (isJobStatus(next)) setStatus(next);
              }}
            >
              <SelectTrigger className="sm:w-56" aria-label="Status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUS_FILTERS.map((item) => (
                  <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Clear search and status"
              disabled={query.length === 0 && status === null}
              onClick={() => {
                setQuery('');
                setDebouncedQuery('');
                setStatus(null);
              }}
            >
              <Eraser aria-hidden="true" />
            </Button>
          </div>
          <RecentGrid scope="all" query={debouncedQuery} status={status} />
        </section>
      </main>
    </div>
  );
}

